import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import type { DashDeviceInfoLabelItem } from 'src/helpers/dash_device_info_labels';
import {
	input_settings_check,
	input_status_check,
	is1LWithMultipleInputs,
} from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';

const isMomentaryInput = (settings: unknown, device: Device): boolean => {
	if (!settings) return false;
	try {
		return !input_settings_check(settings, device);
	} catch {
		return false;
	}
};

/**
 * Power-button bar and card-tap chip share this gate: Redis flag on,
 * not a 1L (those use status labels), not momentary.
 */
export const shouldShowInputStateIdentifier = (
	device: Device | undefined | null,
	settings: unknown,
): boolean => {
	if (!device?.input_state) return false;
	if (is1LWithMultipleInputs(device.type)) return false;
	if (isMomentaryInput(settings, device)) return false;
	return true;
};

export const getDeviceButtonInputIndicator = (
	device: Device | undefined | null,
	status: unknown,
	settings: unknown,
): 'on' | 'off' | undefined => {
	if (!shouldShowInputStateIdentifier(device, settings) || !status) return undefined;
	return input_status_check(status, device) ? 'on' : 'off';
};

export const getInputStateInfoLabel = (
	device: Device | undefined | null,
	status: { online?: boolean } | undefined | null,
	settings: unknown,
): DashDeviceInfoLabelItem | null => {
	if (!shouldShowInputStateIdentifier(device, settings) || !status?.online) return null;

	const isOn = !!input_status_check(status, device);
	const _format = get(format);
	const value = isOn
		? _format('input_state_chip_on', { default: 'Input: On' })
		: _format('input_state_chip_off', { default: 'Input: Off' });

	return {
		name: 'input_state',
		value,
		icon: 'fal fa-plug',
		noBackground: true,
		noPadding: true,
	};
};
